import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PlaceIcon from "@mui/icons-material/Place";
import GroupsIcon from "@mui/icons-material/Groups";
import axios from "axios";
import { useI18n } from "../../../i18n/I18nContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function formatCounter(value, plus = false) {
    const number = Number(value || 0);
    const formatted = new Intl.NumberFormat("de-CH").format(number).replace(/\s/g, "'");
    return plus ? `${formatted}+` : formatted;
}

export default function HomeStatsBar() {
    const { t } = useI18n();
    const [stats, setStats] = React.useState({
        hotels: 0,
        restaurants: 0,
        countries: 0,
        happy_guests: 50000,
    });
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let active = true;

        axios
            .get(`${API_BASE}/api/filteredcompanies/stats`, { withCredentials: true })
            .then((res) => {
                if (active) {
                    setStats({
                        hotels: res.data?.hotels ?? 0,
                        restaurants: res.data?.restaurants ?? 0,
                        countries: res.data?.countries ?? 0,
                        happy_guests: res.data?.happy_guests ?? 50000,
                    });
                }
            })
            .catch(() => {
                if (active) {
                    setStats((current) => ({ ...current, happy_guests: 50000 }));
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, []);

    const items = [
        { key: "hotels", icon: HotelIcon, value: formatCounter(stats.hotels, true), label: t("home.stats.hotels") },
        { key: "restaurants", icon: RestaurantIcon, value: formatCounter(stats.restaurants, true), label: t("home.stats.restaurants") },
        { key: "countries", icon: PlaceIcon, value: formatCounter(stats.countries), label: t("home.stats.countries") },
        { key: "happy_guests", icon: GroupsIcon, value: formatCounter(stats.happy_guests, true), label: t("home.stats.happyGuests") },
    ];

    return (
        <Box
            sx={{
                bgcolor: "background.paper",
                borderBottom: "1px solid #e5e7eb",
                boxShadow: "0 6px 22px rgba(15,23,42,0.05)",
            }}
        >
            <Box
                sx={{
                    maxWidth: 1540,
                    mx: "auto",
                    px: { xs: 2, md: 4 },
                    py: { xs: 2.5, md: 3 },
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                    alignItems: "center",
                }}
            >
                {items.map((item, index) => {
                    const Icon = item.icon;
                    return (
                        <Box
                            key={item.key}
                            sx={{
                                minHeight: 58,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                px: { xs: 1, md: 2 },
                                borderLeft: {
                                    xs: index % 2 === 1 ? "1px solid #e5e7eb" : "none",
                                    md: index === 0 ? "none" : "1px solid #e5e7eb",
                                },
                                borderTop: {
                                    xs: index > 1 ? "1px solid #e5e7eb" : "none",
                                    md: "none",
                                },
                                pt: { xs: index > 1 ? 2 : 0, md: 0 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    flex: "0 0 52px",
                                    borderRadius: "50%",
                                    display: "grid",
                                    placeItems: "center",
                                    bgcolor: "var(--domain-primary-light)",
                                    color: "var(--domain-primary)",
                                }}
                            >
                                <Icon sx={{ fontSize: 27 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: "var(--domain-text)",
                                        fontSize: { xs: "1.25rem", md: "1.8rem" },
                                        lineHeight: 1,
                                        fontWeight: 800,
                                        letterSpacing: 0,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {loading && item.key !== "happy_guests" ? (
                                        <CircularProgress size={18} sx={{ color: "var(--domain-primary)" }} />
                                    ) : (
                                        item.value
                                    )}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.6,
                                        color: "#374151",
                                        fontSize: { xs: "0.75rem", md: "0.9rem" },
                                        fontWeight: 500,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {item.label}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
